import { useState, useEffect, useRef } from "react";
import { Save, Upload, RefreshCw, Mail, Send, CheckCircle, XCircle, ChevronDown, ChevronUp, Lock, Wifi, WifiOff, Eye, EyeOff, Search, Shield, Palette, Building2, FileText, Settings as SettingsIcon, Check, AlertCircle, ExternalLink, Copy, Sparkles, MapPin, Phone, AtSign, Hash, CreditCard, FileCheck, HardDrive, Clock, Download, Trash2, RotateCcw } from "lucide-react";
import { useToast } from "../hooks/useToast";
import Toast from "../ui/Toast";
import LogoUpload from "../components/LogoUpload";
import RestorePreviewDialog from "../components/RestorePreviewDialog";

/**
 * Settings Page - ULTIMATE 2026 Premium SaaS UI
 * Full-featured: Collapsible sections, side nav, search, sticky save bar, live preview, smart UX
 */
export default function SettingsPageUltimate2026() {
  console.log('🚀 ULTIMATE SETTINGS PAGE 2026 LOADED!');
  
  const { toast, hideToast, success, error: showError, info } = useToast();
  
  // State
  const [theme, setTheme] = useState(null);
  const [smtp, setSmtp] = useState(null);
  const [originalTheme, setOriginalTheme] = useState(null);
  const [originalSmtp, setOriginalSmtp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [showAdvancedSmtp, setShowAdvancedSmtp] = useState(false);
  const [smtpProvider, setSmtpProvider] = useState("custom");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showPassword, setShowPassword] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [smtpConnectionStatus, setSmtpConnectionStatus] = useState(null);
  
  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    security: true,
    email: true,
    branding: true,
    company: true,
    layout: true,
    footer: true,
    sync: true,
    backup: true,
  });
  
  const [activeSection, setActiveSection] = useState('security');
  
  // Refs for scrolling
  const sectionRefs = {
    security: useRef(null),
    email: useRef(null),
    branding: useRef(null),
    company: useRef(null),
    layout: useRef(null),
    footer: useRef(null),
    sync: useRef(null),
    backup: useRef(null),
  };

  const hasUnsavedChanges = () => {
    if (!theme || !smtp || !originalTheme || !originalSmtp) return false;
    return JSON.stringify(theme) !== JSON.stringify(originalTheme) ||
           JSON.stringify(smtp) !== JSON.stringify(originalSmtp);
  };

  useEffect(() => {
    loadSettings();
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle URL hash navigation for deep linking
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && sectionRefs[hash]) {
        // Set active section
        setActiveSection(hash);
        // Expand the section if it's collapsed
        setExpandedSections(prev => ({ ...prev, [hash]: true }));
        // Scroll to section after a brief delay to ensure it's rendered
        setTimeout(() => {
          scrollToSection(hash);
        }, 100);
      }
    };

    // Handle initial hash on page load
    handleHashChange();
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Map dashboard section names to settings section names
  const sectionMapping = {
    'company': 'company',
    'email': 'email',
    'branding': 'branding', 
    'layout': 'layout',
    'footer': 'footer'
  };

  // Scroll spy
  useEffect(() => {
    const scrollContainer = document.querySelector('.settings-scroll-container');
    if (!scrollContainer) return;

    const handleScroll = () => {
      const sections = Object.keys(sectionRefs);
      let foundActive = false;
      
      for (const section of sections) {
        const ref = sectionRefs[section].current;
        if (ref) {
          const rect = ref.getBoundingClientRect();
          // Check if section is in viewport (considering the fixed header)
          if (rect.top >= 100 && rect.top <= 300 && !foundActive) {
            setActiveSection(section);
            foundActive = true;
            break;
          }
        }
      }
      
      // If no section is in the trigger zone, find the closest one
      if (!foundActive) {
        let closestSection = null;
        let closestDistance = Infinity;
        
        for (const section of sections) {
          const ref = sectionRefs[section].current;
          if (ref) {
            const rect = ref.getBoundingClientRect();
            const distance = Math.abs(rect.top - 200);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestSection = section;
            }
          }
        }
        
        if (closestSection) {
          setActiveSection(closestSection);
        }
      }
    };
    
    scrollContainer.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();
    
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      
      // Import settings service
      const { default: settingsService } = await import('../services/settings.service');
      
      const [activeTheme, smtpSettings] = await Promise.all([
        settingsService.getActiveTheme(),
        settingsService.getSmtpSettings(),
      ]);
      
      setTheme(activeTheme);
      setOriginalTheme(JSON.parse(JSON.stringify(activeTheme)));
      
      const settings = smtpSettings || {
        smtp_host: "", smtp_port: 587, smtp_secure: false,
        smtp_user: "", smtp_password: "", from_email: "",
        from_name: "", reply_to: "", enabled: false,
      };
      
      setSmtp(settings);
      setOriginalSmtp(JSON.parse(JSON.stringify(settings)));
      
      if (settings.smtp_host?.includes('gmail')) setSmtpProvider('gmail');
      else if (settings.smtp_host?.includes('outlook')) setSmtpProvider('outlook');
      else if (settings.smtp_host) { setSmtpProvider('custom'); setShowAdvancedSmtp(true); }
      
      if (settings.enabled && settings.smtp_host && settings.smtp_user) {
        checkSmtpStatus(settings);
      }
    } catch (err) {
      console.error('[SettingsPage] Failed to load settings:', err);
      showError("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const checkSmtpStatus = async (settings) => {
    try {
      const { default: settingsService } = await import('../services/settings.service');
      const result = await settingsService.testSmtp(settings);
      setSmtpConnectionStatus(result.success ? 'connected' : 'not-verified');
    } catch {
      setSmtpConnectionStatus('not-verified');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Import settings service
      const { default: settingsService } = await import('../services/settings.service');
      
      // Use combined update to avoid document conflicts
      await settingsService.updateAllSettings(theme, smtp);
      
      setOriginalTheme(JSON.parse(JSON.stringify(theme)));
      setOriginalSmtp(JSON.parse(JSON.stringify(smtp)));
      success("Settings saved successfully!");
    } catch (err) {
      console.error('[SettingsPage] Failed to save settings:', err);
      showError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (confirm("Discard all unsaved changes?")) {
      setTheme(JSON.parse(JSON.stringify(originalTheme)));
      setSmtp(JSON.parse(JSON.stringify(originalSmtp)));
      info("Changes discarded");
    }
  };

  const handleTestSmtp = async () => {
    if (!isOnline) {
      showError("You're offline. Connect to test email settings.");
      return;
    }
    try {
      setTestingSmtp(true);
      setSmtpTestResult(null);
      
      const { default: settingsService } = await import('../services/settings.service');
      const result = await settingsService.testSmtp(smtp);
      
      setSmtpTestResult(result);
      setSmtpConnectionStatus(result.success ? 'connected' : 'not-verified');
      if (result.success) success("Connection successful!");
      else showError(getHumanReadableError(result.message));
    } catch (err) {
      const errorMsg = getHumanReadableError(err.message);
      showError(errorMsg);
      setSmtpTestResult({ success: false, message: errorMsg });
      setSmtpConnectionStatus('not-verified');
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleProviderChange = (provider) => {
    setSmtpProvider(provider);
    const presets = {
      gmail: { smtp_host: 'smtp.gmail.com', smtp_port: 587, smtp_secure: false },
      outlook: { smtp_host: 'smtp-mail.outlook.com', smtp_port: 587, smtp_secure: false },
      custom: { smtp_host: smtp.smtp_host || '', smtp_port: smtp.smtp_port || 587, smtp_secure: smtp.smtp_secure || false },
    };
    if (presets[provider]) setSmtp({ ...smtp, ...presets[provider] });
    if (provider === 'custom') setShowAdvancedSmtp(true);
  };

  const getHumanReadableError = (err) => {
    const e = (err || '').toLowerCase();
    if (e.includes('auth')) return "Authentication failed. Check email/password (use App Password for Gmail).";
    if (e.includes('connect')) return "Could not connect. Check internet and server settings.";
    if (e.includes('timeout')) return "Connection timed out. Check internet.";
    if (e.includes('host')) return "Server not found. Check SMTP host.";
    if (e.includes('not available')) return "SMTP testing is not available in web version.";
    return "Connection failed. Verify settings.";
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) { showError("Enter test email"); return; }
    try {
      setTestingSmtp(true);
      info("Sending test email...");
      
      const { default: settingsService } = await import('../services/settings.service');
      const result = await settingsService.sendTestEmail(testEmail);
      
      if (result.success) success(`Test email sent to ${testEmail}!`);
      else showError(`Failed: ${result.message}`);
    } catch (err) {
      showError(`Failed: ${err.message}`);
    } finally {
      setTestingSmtp(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const scrollToSection = (section) => {
    sectionRefs[section]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(section);
  };

  const shouldShowSection = (sectionName) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const keywords = {
      security: ['security', 'pin', 'password', '2fa'],
      email: ['email', 'smtp', 'gmail', 'outlook', 'mail'],
      branding: ['branding', 'logo', 'color', 'font'],
      company: ['company', 'business', 'address', 'gstin'],
      layout: ['layout', 'invoice', 'gst', 'hsn'],
      footer: ['footer', 'terms', 'payment'],
      sync: ['sync', 'synchronize', 'cloud', 'online', 'offline', 'real-time'],
      backup: ['backup', 'restore', 'export', 'import', 'download'],
    };
    return keywords[sectionName]?.some(k => k.includes(q));
  };

  const validateGSTIN = (value) => {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-slate-600 font-medium">Loading settings...</div>
        </div>
      </div>
    );
  }

  if (!theme || !smtp) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-slate-500">No settings found</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-slate-50 to-indigo-50/20">
      {/* Fixed Header with Search */}
      <div className="bg-white/90 backdrop-blur-xl border-b border-slate-200/60 z-30 shadow-sm flex-shrink-0">
        <div className="max-w-7xl mx-auto px-8 py-4">
          <div className="flex items-center justify-between gap-6">
            {/* Title Section */}
            <div className="flex items-center gap-3">
              <SettingsIcon size={28} className="text-indigo-600 flex-shrink-0" />
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-800 bg-clip-text text-transparent">
                  Settings
                </h1>
                <p className="text-xs text-slate-500 mt-0.5">Manage preferences</p>
              </div>
            </div>
            
            {/* Search Bar */}
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search settings..."
                className="w-full pl-10 pr-10 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <XCircle size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto settings-scroll-container">
        <div className="max-w-7xl mx-auto px-8 py-8 flex gap-8 pb-32">
        {/* Side Navigation */}
        <div className="w-64 flex-shrink-0 sticky top-4 h-fit">
          <div className="bg-white rounded-2xl border border-slate-200/60 p-4 shadow-sm">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-2">
              Quick Navigation
            </div>
            <nav className="space-y-1">
              {shouldShowSection('security') && (
                <NavItem icon={<Shield size={18} />} label="Security" active={activeSection === 'security'} onClick={() => scrollToSection('security')} />
              )}
              {shouldShowSection('email') && (
                <NavItem icon={<Mail size={18} />} label="Email" active={activeSection === 'email'} onClick={() => scrollToSection('email')}
                  badge={smtpConnectionStatus === 'connected' ? 'Connected' : smtpConnectionStatus === 'not-verified' ? 'Not Verified' : null}
                  badgeColor={smtpConnectionStatus === 'connected' ? 'green' : 'red'} />
              )}
              {shouldShowSection('branding') && (
                <NavItem icon={<Palette size={18} />} label="Branding" active={activeSection === 'branding'} onClick={() => scrollToSection('branding')} />
              )}
              {shouldShowSection('company') && (
                <NavItem icon={<Building2 size={18} />} label="Company Info" active={activeSection === 'company'} onClick={() => scrollToSection('company')} />
              )}
              {shouldShowSection('layout') && (
                <NavItem icon={<FileText size={18} />} label="Invoice Layout" active={activeSection === 'layout'} onClick={() => scrollToSection('layout')} />
              )}
              {shouldShowSection('footer') && (
                <NavItem icon={<FileCheck size={18} />} label="Footer & Terms" active={activeSection === 'footer'} onClick={() => scrollToSection('footer')} />
              )}
              {shouldShowSection('sync') && (
                <NavItem icon={<Wifi size={18} />} label="Sync Status" active={activeSection === 'sync'} onClick={() => scrollToSection('sync')} />
              )}
              {shouldShowSection('backup') && (
                <NavItem icon={<HardDrive size={18} />} label="Backup & Restore" active={activeSection === 'backup'} onClick={() => scrollToSection('backup')} />
              )}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 space-y-6">
          {/* Security Section */}
          {shouldShowSection('security') && (
            <CollapsibleSection ref={sectionRefs.security} icon={<Shield size={22} className="text-indigo-600" />} title="Security"
              description="Manage authentication and account security" expanded={expandedSections.security} onToggle={() => toggleSection('security')}>
              <SecuritySection />
            </CollapsibleSection>
          )}

          {/* Email Section */}
          {shouldShowSection('email') && (
            <CollapsibleSection ref={sectionRefs.email} icon={<Mail size={22} className="text-indigo-600" />} title="Email Delivery"
              description="Configure email for invoices and quotations" expanded={expandedSections.email} onToggle={() => toggleSection('email')}
              badge={smtpConnectionStatus === 'connected' ? (
                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-medium text-emerald-700">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>Connected
                </span>
              ) : smtpConnectionStatus === 'not-verified' ? (
                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-red-50 border border-red-200 rounded-lg text-xs font-medium text-red-700">
                  <AlertCircle size={12} />Not Verified
                </span>
              ) : null}>
              <EmailSection smtp={smtp} setSmtp={setSmtp} smtpProvider={smtpProvider} handleProviderChange={handleProviderChange}
                showAdvancedSmtp={showAdvancedSmtp} setShowAdvancedSmtp={setShowAdvancedSmtp} showPassword={showPassword}
                setShowPassword={setShowPassword} isOnline={isOnline} testingSmtp={testingSmtp} smtpTestResult={smtpTestResult}
                handleTestSmtp={handleTestSmtp} testEmail={testEmail} setTestEmail={setTestEmail} handleSendTestEmail={handleSendTestEmail}
                smtpConnectionStatus={smtpConnectionStatus} />
            </CollapsibleSection>
          )}

          {/* Branding Section */}
          {shouldShowSection('branding') && (
            <CollapsibleSection ref={sectionRefs.branding} icon={<Palette size={22} className="text-indigo-600" />} title="Branding"
              description="Customize your visual identity" expanded={expandedSections.branding} onToggle={() => toggleSection('branding')}>
              <BrandingSection theme={theme} setTheme={setTheme} />
            </CollapsibleSection>
          )}

          {/* Company Section */}
          {shouldShowSection('company') && (
            <CollapsibleSection ref={sectionRefs.company} icon={<Building2 size={22} className="text-indigo-600" />} title="Company Information"
              description="Business details and contact info" expanded={expandedSections.company} onToggle={() => toggleSection('company')}>
              <CompanySection theme={theme} setTheme={setTheme} validateGSTIN={validateGSTIN} />
            </CollapsibleSection>
          )}

          {/* Layout Section */}
          {shouldShowSection('layout') && (
            <CollapsibleSection ref={sectionRefs.layout} icon={<FileText size={22} className="text-indigo-600" />} title="Invoice Layout Options"
              description="Control invoice display settings" expanded={expandedSections.layout} onToggle={() => toggleSection('layout')}>
              <LayoutSection theme={theme} setTheme={setTheme} />
            </CollapsibleSection>
          )}

          {/* Footer Section */}
          {shouldShowSection('footer') && (
            <CollapsibleSection ref={sectionRefs.footer} icon={<FileCheck size={22} className="text-indigo-600" />} title="Footer & Terms"
              description="Payment instructions and terms" expanded={expandedSections.footer} onToggle={() => toggleSection('footer')}>
              <FooterSection theme={theme} setTheme={setTheme} />
            </CollapsibleSection>
          )}

          {/* Sync Section */}
          {shouldShowSection('sync') && (
            <CollapsibleSection ref={sectionRefs.sync} icon={<Wifi size={22} className="text-indigo-600" />} title="Sync Status"
              description="Real-time data synchronization" expanded={expandedSections.sync} onToggle={() => toggleSection('sync')}>
              <SyncSection />
            </CollapsibleSection>
          )}

          {/* Backup Section */}
          {shouldShowSection('backup') && (
            <CollapsibleSection ref={sectionRefs.backup} icon={<HardDrive size={22} className="text-indigo-600" />} title="Backup & Restore"
              description="Protect your data with backups" expanded={expandedSections.backup} onToggle={() => toggleSection('backup')}>
              <BackupSection success={success} showError={showError} info={info} />
            </CollapsibleSection>
          )}
        </div>
        </div>
      </div>

      {/* Sticky Save Bar */}
      {hasUnsavedChanges() && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-slate-200 shadow-2xl z-40 animate-slideUp">
          <div className="max-w-7xl mx-auto px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">Unsaved Changes</div>
                  <div className="text-xs text-slate-600">Changes will be lost if you navigate away</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={handleReset} disabled={saving}
                  className="px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-xl transition-all disabled:opacity-50">
                  Discard
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="px-6 py-2.5 text-sm font-medium bg-gradient-to-r from-indigo-600 to-indigo-700 text-white hover:from-indigo-700 hover:to-indigo-800 rounded-xl transition-all shadow-lg shadow-indigo-500/30 disabled:opacity-50 flex items-center gap-2 hover:scale-105 active:scale-95">
                  {saving ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Saving...</>
                  ) : (
                    <><Check size={16} />Save Changes</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast type={toast.type} message={toast.message} onClose={hideToast} duration={toast.duration} />}
    </div>
  );
}

/* ========== HELPER COMPONENTS ========== */
import React from 'react';

const CollapsibleSection = React.forwardRef(({ icon, title, description, expanded, onToggle, badge, children }, ref) => (
  <div ref={ref} className="bg-white rounded-2xl border border-slate-200/60 shadow-sm hover:shadow-md transition-all">
    <button onClick={onToggle} className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50/50 transition-colors rounded-t-2xl">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl flex items-center justify-center">{icon}</div>
        <div className="text-left">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
            {badge}
          </div>
          <p className="text-sm text-slate-600 mt-0.5">{description}</p>
        </div>
      </div>
      <div className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>
        <ChevronDown size={20} className="text-slate-400" />
      </div>
    </button>
    {expanded && <div className="px-6 pb-6 pt-2 border-t border-slate-100 animate-slideDown">{children}</div>}
  </div>
));

function NavItem({ icon, label, active, onClick, badge, badgeColor }) {
  return (
    <button onClick={onClick} className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl transition-all ${
      active ? 'bg-gradient-to-r from-indigo-50 to-indigo-100 text-indigo-700 shadow-sm' : 'text-slate-600 hover:bg-slate-50'
    }`}>
      <div className="flex items-center gap-3">{icon}<span className="text-sm font-medium">{label}</span></div>
      {badge && <span className={`text-xs px-2 py-0.5 rounded-full ${badgeColor === 'green' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{badge}</span>}
    </button>
  );
}

/* ========== SECURITY SECTION ========== */
function SecuritySection() {
  const [pinStatus, setPinStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const isElectron = typeof window !== 'undefined' && window.electron;

  useEffect(() => {
    (async () => {
      try {
        if (!isElectron) {
          // PIN is only available in desktop app
          setPinStatus({ enabled: false, isWebApp: true });
          setLoading(false);
          return;
        }
        const status = await window.electron.invoke('auth:getPinStatus');
        setPinStatus(status);
      } catch (err) {
        console.error('Failed to load PIN:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [isElectron]);

  const handleResetPin = async () => {
    if (!isElectron) {
      setResetError('PIN feature is only available in the desktop app');
      return;
    }
    if (!resetPassword) { setResetError('Password required'); return; }
    setResetLoading(true);
    setResetError('');
    try {
      await window.electron.invoke('auth:disablePin', { password: resetPassword });
      const status = await window.electron.invoke('auth:getPinStatus');
      setPinStatus(status);
      setShowResetModal(false);
      setResetPassword('');
      alert('PIN reset successfully');
    } catch (err) {
      setResetError(err.message || 'Failed to reset PIN');
    } finally {
      setResetLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-slate-300 border-t-indigo-600 rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-4">
      <div className="p-5 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl border border-slate-200/60">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-xl flex items-center justify-center">
              <Lock size={20} className="text-indigo-600" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900">PIN Authentication</div>
              <div className="text-xs text-slate-600 mt-0.5">{pinStatus?.isPinEnabled ? 'Quick 4-digit PIN enabled' : 'Not configured'}</div>
            </div>
          </div>
          {pinStatus?.isPinEnabled ? (
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-medium text-emerald-700">
              <CheckCircle size={14} />Enabled
            </span>
          ) : (
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-xs font-medium text-slate-600">
              <XCircle size={14} />Disabled
            </span>
          )}
        </div>
        {pinStatus?.isPinEnabled ? (
          <button onClick={() => setShowResetModal(true)} className="w-full px-4 py-2.5 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-xl transition-all">
            Reset PIN
          </button>
        ) : (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-xs text-blue-700">Set up PIN from login screen when logging in with password.</p>
          </div>
        )}
      </div>

      {/* Coming Soon Features */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl border border-slate-200/60 opacity-60">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={18} className="text-slate-500" />
            <div className="text-sm font-semibold text-slate-900">Change Password</div>
          </div>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded-lg text-xs text-slate-600">
            <Sparkles size={10} />Coming Soon
          </span>
        </div>
        <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl border border-slate-200/60 opacity-60">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={18} className="text-slate-500" />
            <div className="text-sm font-semibold text-slate-900">Two-Factor Auth</div>
          </div>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded-lg text-xs text-slate-600">
            <Sparkles size={10} />Coming Soon
          </span>
        </div>
      </div>

      {/* Reset Modal */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 animate-slideUp p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-rose-100 rounded-2xl flex items-center justify-center">
                <Lock size={22} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Reset PIN</h3>
                <p className="text-sm text-slate-600">Enter password to confirm</p>
              </div>
            </div>
            {resetError && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800">{resetError}</div>}
            <input type="password" value={resetPassword} onChange={(e) => { setResetPassword(e.target.value); setResetError(''); }}
              placeholder="Enter password" className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 mb-4" autoFocus />
            <div className="flex gap-3">
              <button onClick={() => { setShowResetModal(false); setResetPassword(''); setResetError(''); }} disabled={resetLoading}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleResetPin} disabled={resetLoading || !resetPassword}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                {resetLoading ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Resetting...</> : 'Reset PIN'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========== EMAIL SECTION ========== */
function EmailSection({ smtp, setSmtp, smtpProvider, handleProviderChange, showAdvancedSmtp, setShowAdvancedSmtp, showPassword, setShowPassword, isOnline, testingSmtp, smtpTestResult, handleTestSmtp, testEmail, setTestEmail, handleSendTestEmail, smtpConnectionStatus }) {
  return (
    <div className="space-y-6">
      {/* Status Banner */}
      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/50 rounded-xl">
        <div className="flex items-start gap-3">
          <Mail size={18} className="text-blue-600 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-blue-900 mb-1">Global Email Setup</div>
            <div className="text-xs text-blue-700">Used for: <strong>Invoices</strong> • <strong>Quotations</strong> • <strong>Documents</strong></div>
          </div>
        </div>
      </div>

      {/* Enable Toggle */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-transparent rounded-xl border border-slate-200/60">
        <div>
          <div className="text-sm font-semibold text-slate-900">Enable Email Sending</div>
          <div className="text-xs text-slate-600 mt-1">Automatically send documents via email</div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" checked={smtp.enabled} onChange={(e) => setSmtp({ ...smtp, enabled: e.target.checked })} className="sr-only peer" />
          <div className="w-14 h-7 bg-slate-200 peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-indigo-600 peer-checked:to-indigo-700 shadow-inner"></div>
        </label>
      </div>

      {/* Email Identity */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-slate-900">Email Identity</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">From Name</label>
            <input type="text" value={smtp.from_name} onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })} placeholder="ZyloBill" disabled={!smtp.enabled}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all disabled:bg-slate-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">From Email</label>
            <input type="email" value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} placeholder="billing@company.com" disabled={!smtp.enabled}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all disabled:bg-slate-50" />
          </div>
        </div>
      </div>

      {/* Provider Selection */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-slate-900">Email Provider</h3>
        <div className="grid grid-cols-3 gap-3">
          {['gmail', 'outlook', 'custom'].map(provider => (
            <button key={provider} type="button" onClick={() => handleProviderChange(provider)} disabled={!smtp.enabled}
              className={`p-4 rounded-xl border-2 transition-all text-sm font-medium ${
                smtpProvider === provider ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-indigo-100 text-indigo-700 shadow-lg' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
              } disabled:opacity-50 hover:scale-105 active:scale-95`}>
              {provider.charAt(0).toUpperCase() + provider.slice(1)}
            </button>
          ))}
        </div>

        {/* Gmail Help Link */}
        {smtpProvider === 'gmail' && (
          <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium">
            <ExternalLink size={14} />How to generate Gmail App Password
          </a>
        )}
      </div>

      {/* Credentials */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
          <input type="email" value={smtp.smtp_user} onChange={(e) => setSmtp({ ...smtp, smtp_user: e.target.value })} placeholder="your-email@gmail.com" disabled={!smtp.enabled}
            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all disabled:bg-slate-50" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">{smtpProvider === 'gmail' ? 'App Password' : 'Password'}</label>
          <div className="relative">
            <input type={showPassword ? "text" : "password"} value={smtp.smtp_password} onChange={(e) => setSmtp({ ...smtp, smtp_password: e.target.value })} placeholder="••••••••" disabled={!smtp.enabled}
              className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all disabled:bg-slate-50" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} disabled={!smtp.enabled}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {smtpProvider === 'gmail' && <p className="text-xs text-amber-600 mt-2 flex items-center gap-1"><AlertCircle size={12} />Use 16-character App Password</p>}
        </div>
      </div>

      {/* Advanced Settings */}
      <button type="button" onClick={() => setShowAdvancedSmtp(!showAdvancedSmtp)} disabled={!smtp.enabled}
        className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium disabled:opacity-50">
        <div className={`transition-transform ${showAdvancedSmtp ? 'rotate-180' : ''}`}><ChevronDown size={16} /></div>
        Advanced Settings
      </button>

      {showAdvancedSmtp && (
        <div className="p-5 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl border border-slate-200/60 space-y-4 animate-slideDown">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">SMTP Host</label>
              <input type="text" value={smtp.smtp_host} onChange={(e) => setSmtp({ ...smtp, smtp_host: e.target.value })} placeholder="smtp.gmail.com" disabled={!smtp.enabled}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all disabled:bg-slate-50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">SMTP Port</label>
              <input type="number" value={smtp.smtp_port} onChange={(e) => setSmtp({ ...smtp, smtp_port: parseInt(e.target.value) || 587 })} placeholder="587" disabled={!smtp.enabled}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all disabled:bg-slate-50" />
            </div>
          </div>
        </div>
      )}

      {/* Test Connection */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-900">Test Connection</div>
            <div className="text-xs text-slate-600 mt-1">Verify settings before saving</div>
          </div>
          <button onClick={handleTestSmtp} disabled={testingSmtp || !smtp.enabled || !smtp.smtp_host || !smtp.smtp_user || !isOnline}
            className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-white hover:bg-indigo-600 border border-indigo-600 rounded-xl transition-all disabled:opacity-50 flex items-center gap-2">
            {testingSmtp ? <><RefreshCw size={14} className="animate-spin" />Testing...</> : <><Send size={14} />Test Connection</>}
          </button>
        </div>

        {!isOnline && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
            <WifiOff size={18} className="text-amber-600 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-amber-900">You're offline</div>
              <div className="text-xs text-amber-700 mt-1">Connect to test email settings</div>
            </div>
          </div>
        )}

        {smtpTestResult && (
          <div className={`p-4 rounded-xl border flex items-start gap-3 ${smtpTestResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
            {smtpTestResult.success ? <CheckCircle size={18} className="text-emerald-600 mt-0.5" /> : <XCircle size={18} className="text-red-600 mt-0.5" />}
            <div className="flex-1">
              <div className={`text-sm font-semibold ${smtpTestResult.success ? 'text-emerald-900' : 'text-red-900'}`}>
                {smtpTestResult.success ? 'Connected successfully ✓' : 'Connection failed'}
              </div>
              <div className={`text-xs mt-1 ${smtpTestResult.success ? 'text-emerald-700' : 'text-red-700'}`}>{smtpTestResult.message}</div>
            </div>
          </div>
        )}

        {smtpTestResult?.success && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="text-sm font-semibold text-blue-900 mb-3">Send Test Email</div>
            <div className="flex gap-2">
              <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@example.com"
                className="flex-1 px-4 py-2.5 border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
              <button onClick={handleSendTestEmail} disabled={testingSmtp || !testEmail}
                className="px-4 py-2.5 text-sm font-medium bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 rounded-xl transition-all disabled:opacity-50 flex items-center gap-2">
                <Send size={14} />Send
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ========== BRANDING SECTION WITH LIVE PREVIEW ========== */
function BrandingSection({ theme, setTheme }) {
  const [copiedColor, setCopiedColor] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving', 'saved', null
  const [hoveredFont, setHoveredFont] = useState(null);
  
  const colorPresets = [
    { name: 'Indigo', value: '#6366f1' },
    { name: 'Purple', value: '#a855f7' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Emerald', value: '#10b981' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Rose', value: '#f43f5e' },
    { name: 'Slate', value: '#475569' },
    { name: 'Amber', value: '#f59e0b' },
  ];

  const handleColorChange = (color) => {
    setTheme({ ...theme, brandColor: color });
    setSaveStatus('saving');
    setTimeout(() => setSaveStatus('saved'), 500);
    setTimeout(() => setSaveStatus(null), 2000);
  };

  const copyColorToClipboard = () => {
    navigator.clipboard.writeText(theme.brandColor);
    setCopiedColor(true);
    setTimeout(() => setCopiedColor(false), 2000);
  };

  const isValidHex = (color) => /^#[0-9A-F]{6}$/i.test(color);

  const handleResetBranding = () => {
    if (confirm('Reset branding to default values?')) {
      setTheme({
        ...theme,
        brandColor: '#6366f1',
        fontFamily: 'Inter',
        logoUrl: null,
        logoPath: null,
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Auto-save indicator */}
      {saveStatus && (
        <div className="flex items-center justify-end gap-2 text-sm animate-slideDown">
          {saveStatus === 'saving' ? (
            <><div className="w-3 h-3 border-2 border-slate-300 border-t-indigo-600 rounded-full animate-spin"></div><span className="text-slate-600">Saving...</span></>
          ) : (
            <><CheckCircle size={16} className="text-emerald-600" /><span className="text-emerald-600 font-medium">Saved ✓</span></>
          )}
        </div>
      )}

      {/* Brand Color */}
      <div className="p-6 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200/60 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center justify-between mb-4">
            <label className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Palette size={18} className="text-indigo-600" />
              Brand Color
            </label>
            <button
              onClick={copyColorToClipboard}
              className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-indigo-50 transition-all"
              title="Copy color code"
            >
              {copiedColor ? <><Check size={12} />Copied!</> : <><Copy size={12} />Copy</>}
            </button>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            {/* Color Picker Swatch */}
            <div className="relative group">
              <input
                type="color"
                value={theme.brandColor}
                onChange={(e) => handleColorChange(e.target.value)}
                className="h-14 w-14 rounded-xl border-2 border-slate-300 cursor-pointer hover:border-indigo-500 transition-all hover:scale-105 shadow-md"
                style={{ 
                  boxShadow: `0 0 0 3px ${theme.brandColor}20`,
                  padding: '0',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none'
                }}
              />
              <div className="absolute inset-0 rounded-xl ring-2 ring-transparent group-hover:ring-indigo-400 transition-all pointer-events-none"></div>
            </div>
            
            {/* Hex Input with Preview */}
            <div className="flex-1 relative">
              <input
                type="text"
                value={theme.brandColor}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.startsWith('#') && val.length <= 7) {
                    setTheme({ ...theme, brandColor: val });
                    if (isValidHex(val)) {
                      setSaveStatus('saving');
                      setTimeout(() => setSaveStatus('saved'), 500);
                      setTimeout(() => setSaveStatus(null), 2000);
                    }
                  }
                }}
                placeholder="#6366f1"
                className={`w-full px-4 pr-10 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-mono transition-all ${
                  isValidHex(theme.brandColor) ? 'border-slate-300' : 'border-amber-300 bg-amber-50'
                }`}
              />
              {!isValidHex(theme.brandColor) && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <AlertCircle size={16} className="text-amber-600" />
                </div>
              )}
            </div>
          </div>

          {/* Color Presets */}
          <div>
            <div className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-2">
              <Sparkles size={12} />Quick Presets
            </div>
            <div className="grid grid-cols-8 gap-2">
              {colorPresets.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => handleColorChange(preset.value)}
                  className="group relative h-10 rounded-lg border-2 transition-all hover:scale-110 hover:shadow-lg"
                  style={{
                    backgroundColor: preset.value,
                    borderColor: theme.brandColor === preset.value ? preset.value : 'transparent',
                    boxShadow: theme.brandColor === preset.value ? `0 0 0 2px ${preset.value}40` : 'none',
                  }}
                  title={preset.name}
                >
                  {theme.brandColor === preset.value && (
                    <Check size={14} className="absolute inset-0 m-auto text-white drop-shadow-lg" />
                  )}
                  <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-slate-600 whitespace-nowrap">
                    {preset.name}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-4 flex items-center gap-1">
            <AlertCircle size={12} />
            Used for company name and invoice title
          </p>
        </div>

        {/* Font Family */}
        <div className="p-6 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200/60 shadow-sm hover:shadow-md transition-all">
          <label className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <FileText size={18} className="text-indigo-600" />
            Font Family
          </label>
          <select
            value={theme.fontFamily}
            onChange={(e) => {
              setTheme({ ...theme, fontFamily: e.target.value });
              setSaveStatus('saving');
              setTimeout(() => setSaveStatus('saved'), 500);
              setTimeout(() => setSaveStatus(null), 2000);
            }}
            onMouseOver={(e) => {
              const option = e.target.options[e.target.selectedIndex];
              if (option) setHoveredFont(option.value);
            }}
            onMouseOut={() => setHoveredFont(null)}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all bg-white hover:border-indigo-400 cursor-pointer"
            style={{ fontFamily: hoveredFont || theme.fontFamily }}
          >
            <option value="Inter" style={{ fontFamily: 'Inter' }}>
              Inter — Modern & Clean ⭐ Recommended
            </option>
            <option value="Roboto" style={{ fontFamily: 'Roboto' }}>
              Roboto — Professional
            </option>
            <option value="Serif" style={{ fontFamily: 'serif' }}>
              Serif — Traditional
            </option>
          </select>
          <p className="text-xs text-slate-500 mt-3">Preview updates as you hover over options</p>
        </div>

        {/* Company Logo */}
        <div className="p-6 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200/60 shadow-sm hover:shadow-md transition-all">
          <label className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Upload size={18} className="text-indigo-600" />
            Company Logo
          </label>
          <LogoUpload
            logoUrl={theme.logoUrl}
            onLogoChange={(logoUrl, logoPath) => {
              setTheme({ ...theme, logoUrl, logoPath });
              setSaveStatus('saving');
              setTimeout(() => setSaveStatus('saved'), 500);
              setTimeout(() => setSaveStatus(null), 2000);
            }}
            onLogoRemove={() => setTheme({ ...theme, logoUrl: null, logoPath: null })}
          />
        </div>

        {/* Reset Button */}
        <button
          onClick={handleResetBranding}
          className="w-full px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded-xl transition-all flex items-center justify-center gap-2"
        >
          <RefreshCw size={14} />
          Reset to Default Branding
        </button>
    </div>
  );
}

/* ========== COMPANY SECTION WITH GSTIN VALIDATION ========== */
function CompanySection({ theme, setTheme, validateGSTIN }) {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
          <Building2 size={16} />Company Name
        </label>
        <input type="text" value={theme.company.name} onChange={(e) => setTheme({ ...theme, company: { ...theme.company, name: e.target.value } })}
          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all" />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
          <MapPin size={16} />Address
        </label>
        <input type="text" value={theme.company.address} onChange={(e) => setTheme({ ...theme, company: { ...theme.company, address: e.target.value } })}
          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all" />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">City</label>
          <input type="text" value={theme.company.city} onChange={(e) => setTheme({ ...theme, company: { ...theme.company, city: e.target.value } })}
            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
          <input type="text" value={theme.company.state} onChange={(e) => setTheme({ ...theme, company: { ...theme.company, state: e.target.value } })}
            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Pincode</label>
          <input type="text" value={theme.company.pincode} onChange={(e) => setTheme({ ...theme, company: { ...theme.company, pincode: e.target.value } })}
            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all" />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
          <Hash size={16} />GSTIN
        </label>
        <input type="text" value={theme.company.gstin} 
          onChange={(e) => setTheme({ ...theme, company: { ...theme.company, gstin: validateGSTIN(e.target.value) } })}
          placeholder="22AAAAA0000A1Z5" maxLength={15}
          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all uppercase" />
        <p className="text-xs text-slate-500 mt-2">15-character GST Identification Number (auto-formatted)</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
            <AtSign size={16} />Email
          </label>
          <input type="email" value={theme.company.email} onChange={(e) => setTheme({ ...theme, company: { ...theme.company, email: e.target.value } })}
            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
            <Phone size={16} />Phone
          </label>
          <input type="tel" value={theme.company.phone} onChange={(e) => setTheme({ ...theme, company: { ...theme.company, phone: e.target.value } })}
            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all" />
        </div>
      </div>
    </div>
  );
}

/* ========== LAYOUT SECTION ========== */
function LayoutSection({ theme, setTheme }) {
  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-slate-700 mb-3">Tax Settings</div>
      <ToggleOption label="Show GST Breakdown" description="Display CGST/SGST/IGST columns and totals"
        checked={theme.showGstBreakdown} onChange={(checked) => setTheme({ ...theme, showGstBreakdown: checked })} />
      <ToggleOption label="Show HSN/SAC Column" description="Display HSN/SAC codes in items table"
        checked={theme.showHsn} onChange={(checked) => setTheme({ ...theme, showHsn: checked })} />
      
      <div className="border-t border-slate-200 my-6"></div>
      
      <div className="text-sm font-semibold text-slate-700 mb-3">Display Settings</div>
      <ToggleOption label="Show Signature Line" description="Add authorized signature line at bottom"
        checked={theme.showSignature} onChange={(checked) => setTheme({ ...theme, showSignature: checked })} />
    </div>
  );
}

function ToggleOption({ label, description, checked, onChange }) {
  return (
    <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200/60 hover:border-slate-300 transition-all cursor-pointer group">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-5 h-5 text-indigo-600 border-slate-300 rounded-lg focus:ring-indigo-500 transition-all" />
      <div className="flex-1">
        <div className="text-sm font-medium text-slate-900 group-hover:text-indigo-600 transition-colors">{label}</div>
        <div className="text-xs text-slate-600 mt-1">{description}</div>
      </div>
    </label>
  );
}

/* ========== FOOTER SECTION WITH TEMPLATES ========== */
function FooterSection({ theme, setTheme }) {
  const [showTemplates, setShowTemplates] = useState(false);
  
  const templates = {
    standard: "Thank you for your business!\n\nFor any queries, please contact us.",
    gst: "This is a computer-generated invoice.\n\nGST Compliance: This invoice is issued as per GST regulations.\n\nThank you for your business!",
    payment: "Payment Terms:\n• Payment due within 30 days\n• Late payment subject to 2% monthly interest\n• All payments in INR only",
  };

  const applyTemplate = (template) => {
    setTheme({ ...theme, termsAndConditions: templates[template] });
    setShowTemplates(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Footer Note</label>
        <textarea value={theme.footerNote} onChange={(e) => setTheme({ ...theme, footerNote: e.target.value })} rows={2}
          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all resize-none" />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Payment Instructions (Optional)</label>
        <textarea value={theme.paymentInstructions || ""} onChange={(e) => setTheme({ ...theme, paymentInstructions: e.target.value })} rows={4}
          placeholder="Bank: HDFC Bank&#10;Account: 1234567890&#10;IFSC: HDFC0001234"
          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all resize-none" />
        <p className="text-xs text-slate-500 mt-2">Bank details, UPI ID, or payment info</p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-slate-700">Terms & Conditions (Optional)</label>
          <button onClick={() => setShowTemplates(!showTemplates)}
            className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1">
            <Copy size={12} />Use Template
          </button>
        </div>
        
        {showTemplates && (
          <div className="mb-3 p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2 animate-slideDown">
            <button onClick={() => applyTemplate('standard')} className="w-full text-left px-3 py-2 text-sm hover:bg-white rounded-lg transition-colors">
              <div className="font-medium text-slate-900">Standard Terms</div>
              <div className="text-xs text-slate-600">Basic thank you message</div>
            </button>
            <button onClick={() => applyTemplate('gst')} className="w-full text-left px-3 py-2 text-sm hover:bg-white rounded-lg transition-colors">
              <div className="font-medium text-slate-900">GST Compliance</div>
              <div className="text-xs text-slate-600">GST-compliant terms</div>
            </button>
            <button onClick={() => applyTemplate('payment')} className="w-full text-left px-3 py-2 text-sm hover:bg-white rounded-lg transition-colors">
              <div className="font-medium text-slate-900">Payment Terms</div>
              <div className="text-xs text-slate-600">Payment conditions</div>
            </button>
          </div>
        )}
        
        <textarea value={theme.termsAndConditions || ""} onChange={(e) => setTheme({ ...theme, termsAndConditions: e.target.value })} rows={4}
          placeholder="1. Payment due within 30 days&#10;2. Late payment subject to interest&#10;3. Goods once sold cannot be returned"
          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm transition-all resize-none" />
      </div>
    </div>
  );
}

/* ========== SYNC SECTION ========== */
function SyncSection() {
  const [syncStatus, setSyncStatus] = useState({ status: 'offline', lastSyncTime: null, error: null });
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    // Import database service
    const loadSyncStatus = async () => {
      try {
        const { default: databaseService } = await import('../services/database.service');
        
        // Get current status
        const status = databaseService.getSyncStatus();
        setSyncStatus(status);
        
        // Subscribe to status changes
        const unsubscribe = databaseService.onSyncStatusChange((statusUpdate) => {
          setSyncStatus(statusUpdate);
        });
        
        return unsubscribe;
      } catch (error) {
        console.error('[Sync UI] Failed to load sync status:', error);
      }
    };
    
    const unsubscribePromise = loadSyncStatus();
    
    return () => {
      unsubscribePromise.then(unsubscribe => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, []);

  const handleManualSync = async () => {
    setSyncing(true);
    try {
      const { default: databaseService } = await import('../services/database.service');
      
      // Trigger a manual sync by getting database info (forces sync check)
      await databaseService.getDatabaseInfo();
      
      console.log('[Sync UI] Manual sync triggered');
    } catch (error) {
      console.error('[Sync UI] Manual sync failed:', error);
    } finally {
      setTimeout(() => setSyncing(false), 1000);
    }
  };

  const formatLastSync = (lastSyncTime) => {
    if (!lastSyncTime) return 'Never';
    
    const now = new Date();
    const syncDate = new Date(lastSyncTime);
    const diffMs = now - syncDate;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffSecs < 10) return 'Just now';
    if (diffSecs < 60) return `${diffSecs}s ago`;
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return syncDate.toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'synced': return 'emerald';
      case 'syncing': return 'blue';
      case 'error': return 'red';
      case 'offline': return 'slate';
      default: return 'slate';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'synced': return <CheckCircle size={20} />;
      case 'syncing': return <RefreshCw size={20} className="animate-spin" />;
      case 'error': return <XCircle size={20} />;
      case 'offline': return <WifiOff size={20} />;
      default: return <WifiOff size={20} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'synced': return 'All changes synced';
      case 'syncing': return 'Syncing changes...';
      case 'error': return 'Sync error';
      case 'offline': return 'Offline';
      default: return 'Unknown';
    }
  };

  const statusColor = getStatusColor(syncStatus.status);

  return (
    <div className="space-y-6">
      {/* Sync Status Card */}
      <div className={`p-6 bg-gradient-to-br from-${statusColor}-50 to-${statusColor}-100/50 rounded-xl border border-${statusColor}-200/60`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 bg-gradient-to-br from-${statusColor}-100 to-${statusColor}-200 rounded-xl flex items-center justify-center text-${statusColor}-600`}>
              {getStatusIcon(syncStatus.status)}
            </div>
            <div>
              <h3 className={`text-base font-semibold text-${statusColor}-900 mb-1`}>
                {getStatusText(syncStatus.status)}
              </h3>
              <p className={`text-sm text-${statusColor}-700`}>
                Last synced: {formatLastSync(syncStatus.lastSyncTime)}
              </p>
            </div>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 bg-${statusColor}-100 rounded-lg`}>
            <div className={`w-2 h-2 bg-${statusColor}-500 rounded-full ${syncStatus.status === 'syncing' ? 'animate-pulse' : ''}`}></div>
            <span className={`text-xs font-medium text-${statusColor}-900 capitalize`}>
              {syncStatus.status}
            </span>
          </div>
        </div>

        {/* Error Message */}
        {syncStatus.error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900">Sync Error</p>
                <p className="text-xs text-red-700 mt-1">{syncStatus.error.message || 'Unknown error occurred'}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sync Actions */}
      <div className="grid grid-cols-2 gap-4">
        {/* Manual Sync */}
        <div className="p-5 bg-white rounded-xl border border-slate-200">
          <div className="flex items-center gap-3 mb-3">
            <RefreshCw size={18} className="text-indigo-600" />
            <h3 className="text-sm font-semibold text-slate-900">Manual Sync</h3>
          </div>
          <p className="text-xs text-slate-600 mb-4">
            Force synchronization with the cloud server
          </p>
          <button
            onClick={handleManualSync}
            disabled={syncing || syncStatus.status === 'syncing'}
            className="w-full px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {syncing || syncStatus.status === 'syncing' ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw size={16} />
                Sync Now
              </>
            )}
          </button>
        </div>

        {/* Sync Info */}
        <div className="p-5 bg-white rounded-xl border border-slate-200">
          <div className="flex items-center gap-3 mb-3">
            <Wifi size={18} className="text-indigo-600" />
            <h3 className="text-sm font-semibold text-slate-900">Auto-Sync</h3>
          </div>
          <p className="text-xs text-slate-600 mb-4">
            Changes sync automatically in real-time
          </p>
          <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 px-3 py-2 rounded-lg border border-emerald-200">
            <CheckCircle size={14} />
            <span className="font-medium">Always Active</span>
          </div>
        </div>
      </div>

      {/* Sync Details */}
      <div className="p-5 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl border border-slate-200/60">
        <h3 className="text-sm font-semibold text-slate-900 mb-4">How Sync Works</h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <CheckCircle size={14} className="text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">Real-time Synchronization</p>
              <p className="text-xs text-slate-600 mt-1">
                All changes are automatically synced to the cloud as you work
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <CheckCircle size={14} className="text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">Bidirectional Sync</p>
              <p className="text-xs text-slate-600 mt-1">
                Changes from other devices are pulled automatically
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <CheckCircle size={14} className="text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">Offline Support</p>
              <p className="text-xs text-slate-600 mt-1">
                Work offline and sync when connection is restored
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50/60 border border-blue-200/50 rounded-xl">
        <div className="flex items-start gap-3">
          <Wifi size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-900 mb-1">
              Continuous Sync Active
            </p>
            <p className="text-xs text-blue-700">
              Your data is continuously synchronized with the cloud. All devices stay up-to-date automatically.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ========== BACKUP SECTION ========== */
function BackupSection({ success: successToast, showError, info }) {
  const [lastBackup, setLastBackup] = useState(null);
  const [backupHistory, setBackupHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [autoBackupStatus, setAutoBackupStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedEntities, setSelectedEntities] = useState(['all']);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const isElectron = typeof window !== 'undefined' && window.electron;

  useEffect(() => {
    loadBackupData();
    loadAutoBackupStatus();
  }, []);

  const loadBackupData = async () => {
    console.log('[Backup UI] Loading backup data...');
    try {
      if (isElectron) {
        // Electron version
        const [last, history, backupStats] = await Promise.all([
          window.electron.invoke('backup:getLastBackup'),
          window.electron.invoke('backup:list'),
          window.electron.invoke('backup:getStats')
        ]);
        
        setLastBackup(last);
        setBackupHistory(history);
        setStats(backupStats);
      } else {
        // Web version
        const { default: webBackupService } = await import('../services/backup.web.service');
        const [last, history, backupStats] = await Promise.all([
          webBackupService.getLastBackup(),
          webBackupService.getBackupHistory(),
          webBackupService.getBackupStats()
        ]);
        
        setLastBackup(last);
        setBackupHistory(history);
        setStats(backupStats);
      }
      
      console.log('[Backup UI] Loaded data:', { lastBackup, backupHistory: backupHistory.length, stats });
    } catch (err) {
      console.error('[Backup UI] Failed to load backup data:', err);
      setError('Failed to load backup information');
    }
  };

  const loadAutoBackupStatus = async () => {
    console.log('[Backup UI] Loading auto-backup status...');
    try {
      if (isElectron) {
        const status = await window.electron.invoke('backup:getAutoBackupStatus');
        setAutoBackupStatus(status);
      } else {
        const { default: webBackupService } = await import('../services/backup.web.service');
        const status = await webBackupService.getAutoBackupStatus();
        setAutoBackupStatus(status);
      }
      console.log('[Backup UI] Auto-backup status:', autoBackupStatus);
    } catch (err) {
      console.error('[Backup UI] Failed to load auto-backup status:', err);
    }
  };

  const handleDownloadBackup = async () => {
    console.log('[Backup UI] Starting backup...', { selectedEntities });
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      if (isElectron) {
        // Electron version
        console.log('[Backup UI] Calling backup:download IPC...');
        const result = await window.electron.invoke('backup:download', {
          entities: selectedEntities
        });
        
        console.log('[Backup UI] Backup result:', result);
        
        if (result.canceled) {
          console.log('[Backup UI] User canceled backup');
        } else if (result.success) {
          console.log('[Backup UI] Backup successful!', result);
          setSuccess('Backup created successfully');
          successToast?.('Backup created successfully');
          await loadBackupData();
        }
      } else {
        // Web version
        console.log('[Backup UI] Using web backup service...');
        const { default: webBackupService } = await import('../services/backup.web.service');
        const result = await webBackupService.downloadBackup({
          entities: selectedEntities
        });
        
        console.log('[Backup UI] Backup result:', result);
        setSuccess(`Backup downloaded: ${result.filename}`);
        successToast?.(`Backup downloaded: ${result.filename}`);
        await loadBackupData();
      }
    } catch (err) {
      console.error('[Backup UI] Backup failed:', err);
      setError(err.message || 'Backup failed');
      showError?.(err.message || 'Backup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreClick = async () => {
    try {
      setError(null);
      setSuccess(null);
      
      if (isElectron) {
        // Electron version - use file dialog
        const result = await window.electron.invoke('backup:chooseFile');
        
        if (result.canceled) {
          console.log('[Backup UI] User canceled file selection');
          return;
        }
        
        console.log('[Backup UI] Selected backup file:', result.filePath);
        
        // Preview restore
        const preview = await window.electron.invoke('backup:previewRestore', result.filePath, {
          entities: ['all']
        });
        
        console.log('[Backup UI] Restore preview:', preview);
        setRestorePreview({ ...preview, filePath: result.filePath });
      } else {
        // Web version - use file input
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json,application/json';
        
        input.onchange = async (e) => {
          const file = e.target.files[0];
          if (!file) return;
          
          console.log('[Backup UI] Selected backup file:', file.name);
          setSelectedFile(file);
          
          // Preview restore
          const { default: webBackupService } = await import('../services/backup.web.service');
          const preview = await webBackupService.previewRestore(file);
          
          console.log('[Backup UI] Restore preview:', preview);
          setRestorePreview({ ...preview, file });
        };
        
        input.click();
      }
      
    } catch (err) {
      console.error('[Backup UI] Restore preview failed:', err);
      setError(err.message || 'Failed to preview backup');
      showError?.(err.message || 'Failed to preview backup');
    }
  };

  const handleRestoreConfirm = async () => {
    if (!restorePreview) return;
    
    setRestoring(true);
    setError(null);
    
    try {
      console.log('[Backup UI] Starting restore...');
      
      if (isElectron) {
        // Electron version
        const result = await window.electron.invoke('backup:restore', restorePreview.filePath, {
          entities: ['all'],
          mode: 'merge'
        });
        
        console.log('[Backup UI] Restore result:', result);
        
        if (result.success) {
          setSuccess('Data restored successfully! Refresh the page to see changes.');
          successToast?.('Data restored successfully! Refresh the page to see changes.');
          setRestorePreview(null);
          await loadBackupData();
        }
      } else {
        // Web version
        const { default: webBackupService } = await import('../services/backup.web.service');
        const result = await webBackupService.restoreFromFile(restorePreview.file, {
          mode: 'merge'
        });
        
        console.log('[Backup UI] Restore result:', result);
        
        if (result.success) {
          const totalImported = Object.values(result.imported).reduce((sum, count) => sum + count, 0);
          setSuccess(`Data restored successfully! Imported ${totalImported} items. Refresh to see changes.`);
          successToast?.(`Data restored successfully! Imported ${totalImported} items.`);
          setRestorePreview(null);
          setSelectedFile(null);
          await loadBackupData();
        }
      }
      
    } catch (err) {
      console.error('[Backup UI] Restore failed:', err);
      setError(err.message || 'Restore failed');
      showError?.(err.message || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const handleRestoreCancel = () => {
    setRestorePreview(null);
  };

  const handleCleanup = async () => {
    if (!confirm('Delete backup history entries older than 30 days?')) return;
    
    try {
      if (isElectron) {
        await window.electron.invoke('backup:cleanup', 30);
      } else {
        const { default: webBackupService } = await import('../services/backup.web.service');
        await webBackupService.cleanup(30);
      }
      
      setSuccess('Old backup history cleaned up successfully');
      successToast?.('Old backup history cleaned up successfully');
      await loadBackupData();
    } catch (err) {
      console.error('Cleanup failed:', err);
      setError('Cleanup failed');
      showError?.('Cleanup failed');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Restore Preview Dialog */}
      {restorePreview && (
        <RestorePreviewDialog
          preview={restorePreview}
          onConfirm={handleRestoreConfirm}
          onCancel={handleRestoreCancel}
          loading={restoring}
        />
      )}

      {/* Success Message */}
      {success && (
        <div className="p-4 bg-emerald-50/80 border border-emerald-200/50 rounded-xl flex items-start gap-3">
          <CheckCircle size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-emerald-900">{success}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-rose-50/80 border border-rose-200/50 rounded-xl flex items-start gap-3">
          <XCircle size={18} className="text-rose-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-rose-900">{error}</p>
          </div>
        </div>
      )}

      {/* Backup Status Card */}
      <div className="p-6 bg-slate-50/60 rounded-xl border border-slate-200">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-base font-semibold text-slate-900 mb-1">
              Backup Status
            </h3>
            <p className="text-sm text-slate-600">
              {autoBackupStatus?.isRunning 
                ? 'Automatic backups run every 6 hours' 
                : 'Your data is backed up manually'}
            </p>
          </div>
          {autoBackupStatus?.isRunning && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50/60 rounded-lg">
              <CheckCircle size={14} className="text-emerald-600" />
              <span className="text-xs font-medium text-emerald-900">Auto-Backup Active</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 bg-white rounded-xl border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={16} className="text-slate-500" />
              <span className="text-xs font-medium text-slate-600">Last Backup</span>
            </div>
            <p className="text-lg font-semibold text-slate-900">
              {formatDate(lastBackup?.created_at)}
            </p>
          </div>

          <div className="p-4 bg-white rounded-xl border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <HardDrive size={16} className="text-slate-500" />
              <span className="text-xs font-medium text-slate-600">Total Backups</span>
            </div>
            <p className="text-lg font-semibold text-slate-900">
              {stats?.totalBackups || 0}
            </p>
          </div>

          <div className="p-4 bg-white rounded-xl border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <HardDrive size={16} className="text-slate-500" />
              <span className="text-xs font-medium text-slate-600">Storage Used</span>
            </div>
            <p className="text-lg font-semibold text-slate-900">
              {formatBytes(stats?.totalSize)}
            </p>
          </div>
        </div>

        {/* Auto-Backup Next Run Info */}
        {autoBackupStatus?.isRunning && autoBackupStatus.nextBackup && (
          <div className="mt-4 p-3 bg-blue-50/60 rounded-lg border border-blue-200/50">
            <div className="flex items-center justify-between text-xs">
              <span className="text-blue-700">Next automatic backup:</span>
              <span className="font-medium text-blue-900">
                {formatDate(autoBackupStatus.nextBackup)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Manual Backup Card */}
        <div className="p-6 bg-white rounded-xl border border-slate-200">
          <h3 className="text-base font-semibold text-slate-900 mb-4">
            Create Manual Backup
          </h3>

          <div className="space-y-3 mb-6">
            <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50/60 cursor-pointer transition-colors">
              <input
                type="radio"
                name="backup-type"
                checked={selectedEntities.includes('all')}
                onChange={() => setSelectedEntities(['all'])}
                className="w-4 h-4 text-indigo-600"
              />
              <div>
                <p className="text-sm font-medium text-slate-900">Full Backup</p>
                <p className="text-xs text-slate-600">All invoices, quotations, clients, and settings</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50/60 cursor-pointer transition-colors">
              <input
                type="radio"
                name="backup-type"
                checked={selectedEntities.includes('invoices') && !selectedEntities.includes('all')}
                onChange={() => setSelectedEntities(['invoices'])}
                className="w-4 h-4 text-indigo-600"
              />
              <div>
                <p className="text-sm font-medium text-slate-900">Invoices Only</p>
                <p className="text-xs text-slate-600">Export all invoices and invoice items</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50/60 cursor-pointer transition-colors">
              <input
                type="radio"
                name="backup-type"
                checked={selectedEntities.includes('clients') && !selectedEntities.includes('all')}
                onChange={() => setSelectedEntities(['clients'])}
                className="w-4 h-4 text-indigo-600"
              />
              <div>
                <p className="text-sm font-medium text-slate-900">Clients Only</p>
                <p className="text-xs text-slate-600">Export all client information</p>
              </div>
            </label>
          </div>

          <button
            onClick={handleDownloadBackup}
            disabled={loading}
            className="w-full px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Creating Backup...
              </>
            ) : (
              <>
                <Download size={16} />
                Download Backup
              </>
            )}
          </button>
        </div>

        {/* Restore Card */}
        <div className="p-6 bg-white rounded-xl border border-slate-200">
          <h3 className="text-base font-semibold text-slate-900 mb-4">
            Restore from Backup
          </h3>

          <div className="mb-6">
            <p className="text-sm text-slate-600 mb-4">
              Restore your data from a previous backup file. Your existing data will be kept safe.
            </p>

            <div className="p-3 bg-emerald-50/60 rounded-lg border border-emerald-200/50">
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-emerald-700">
                  <strong>Safe restore:</strong> Nothing will be deleted. Only new data will be added.
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleRestoreClick}
            disabled={restoring}
            className="w-full px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {restoring ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Restoring...
              </>
            ) : (
              <>
                <RotateCcw size={16} />
                Choose Backup File
              </>
            )}
          </button>
        </div>
      </div>

      {/* Backup History */}
      <div className="p-6 bg-white rounded-xl border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-900">
            Backup History
          </h3>
          {backupHistory.length > 0 && (
            <button
              onClick={handleCleanup}
              className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1.5 transition-colors"
            >
              <Trash2 size={14} />
              Clean Up Old Backups
            </button>
          )}
        </div>

        {backupHistory.length === 0 ? (
          <div className="py-12 text-center">
            <HardDrive size={32} className="mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-600 mb-1">No backups yet</p>
            <p className="text-xs text-slate-500">Create your first backup above</p>
          </div>
        ) : (
          <div className="space-y-2">
            {backupHistory.slice(0, 10).map((backup) => (
              <div
                key={backup.id}
                className="p-4 rounded-xl border border-slate-200/60 hover:bg-slate-50/40 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-slate-900">
                        {backup.backup_type === 'manual' ? 'Manual Backup' : 'Auto Backup'}
                      </p>
                      {backup.status === 'completed' ? (
                        <CheckCircle size={14} className="text-emerald-600" />
                      ) : (
                        <XCircle size={14} className="text-rose-600" />
                      )}
                    </div>
                    <p className="text-xs text-slate-600 mb-2">
                      {new Date(backup.created_at).toLocaleString('en-IN', { 
                        timeZone: 'Asia/Kolkata',
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      })}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span>{formatBytes(backup.file_size)}</span>
                      {backup.entitiesCount && (
                        <>
                          <span>•</span>
                          <span>
                            {backup.entitiesCount.invoices || 0} invoices,{' '}
                            {backup.entitiesCount.clients || 0} clients
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50/60 border border-blue-200/50 rounded-xl">
        <div className="flex items-start gap-3">
          <CheckCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-900 mb-1">
              Your data is safe
            </p>
            <p className="text-xs text-blue-700">
              Backups are stored locally on your computer. You can download them anytime and restore if needed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
